import { FocusEvent, MouseEvent, useCallback, useMemo } from 'react';

import { useTkv2UpdateReferenceBarProperties } from '../../controller/hooks/useTkv2UpdateReferenceBarProperties.ts';
import { Tkv2ReferenceBarProperty } from '../../controller/types.ts';
import { isReferenceViewID } from '../../modules/referenceView/index.ts';
import * as consts from './consts.ts';

/**
 * Searches upward in the DOM hierarchy for elements that have the
 * data-v2-ref-id and data-v2-ref-map attributes
 */
function findReferenceProperty(tgt: EventTarget | null): Tkv2ReferenceBarProperty | null {
  let curr: unknown = tgt;
  while (curr && curr instanceof HTMLElement) {
    const id = curr.getAttribute(consts.tkv2ReferenceViewIDAttr);
    const mapPinLabel = curr.getAttribute(consts.tkv2ReferenceMapPinLabelAttr);
    if (isReferenceViewID(id) && mapPinLabel !== null) {
      return { id, mapPinLabel };
    }
    curr = curr.parentElement;
  }
  return null;
}

type Handlers = {
  onClick: (e: MouseEvent) => void;
  onFocus: (e: FocusEvent) => void;
};

/**
 * Install these event handlers somewhere high up in your DOM hierarchy so click
 * and focus events within the page can bubble up to it.
 */
export function useTkv2ReferenceBarEvents(): Handlers {
  const handlePropertyClick = useTkv2UpdateReferenceBarProperties();

  const handler = useCallback(
    (e: MouseEvent | FocusEvent): void => {
      const property = findReferenceProperty(e.target);
      if (property !== null) {
        handlePropertyClick(property);
      }
    },
    [handlePropertyClick],
  );

  return useMemo(() => ({ onClick: handler, onFocus: handler }), [handler]);
}
